<template src="./App.html"></template>

<style lang="scss" src="./assets/_common/scss/_grid.scss"></style>
<style lang="scss" src="./assets/_common/scss/_mixins.scss"></style>
<style lang="scss" src="./assets/_common/scss/_universal.scss"></style>
<style lang="scss" src="./assets/_common/scss/_layout.scss"></style>
<style lang="scss" src="./assets/_common/scss/_breakpoints.scss"></style>
<style lang="scss" src="./App.scss"></style>

<script>
	import Header from '@/components/Header/Header';
	import Login from '@/components/Login/Login';
	import BlockNavigation from '@/components/BlockNavigation/BlockNavigation';
	import Slider from '@/components/Slider/Slider';
	import Teaser from '@/components/Teaser/Teaser';
	import List from '@/components/List/List';
	import Simple from '@/components/Simple/Simple';
	import Layer from '@/components/Layer/Layer';
	import Footer from '@/components/Footer/Footer';
	import CornerService from '@/components/CornerService/CornerService';
	import { AdServiceInterface } from '@/services/interfaces/adServiceInterface';
	import { AdServiceHelper } from '@/services/helper/adServiceHelper';
	import { CookielascheInterface } from '@/services/interfaces/cookielascheInterface';
	import { TrackingInterface } from '@/services/interfaces/trackingInterface';
	import { TrackingConfig } from '@/config/trackingConfig';

	export default {
		name: 'App',
		components: {
			'hp-teaser': Teaser,
			'hp-block-navigation': BlockNavigation,
			'hp-header': Header,
			'hp-login': Login,
			'hp-slider': Slider,
			'hp-list': List,
			'hp-simple': Simple,
			'hp-footer': Footer,
			'hp-layer': Layer,
			'hp-corner-service': CornerService
		},
		data () {
			return {
				teaserWidth: 300,
				colCount: 1,
				prevColCount: 0,
				dynamicMiddle: false,
				dynamicSides: false,
				fallback: false,
				sticky: false,
				gridRowGap: 30,
				gridColumnGap: 30,
				gridColumnGapMinWidth: 20,
				gridColumnGapActive: true,
				calculatedGridGapWidth: 30,
				gridSize: 1290,
				gridBorder: 30,
				gridBorderMinWidth: 10,
				calculatedGridBorderWidth: 30,
				selectedAd: 'none',
				sitebarWidth: '300',
				skyWidth: '130',
				minOutsideAdWidth: '0',
				adOverlap: true,
				middleName: 'middle',
				rightName: 'right',
				leftName: 'left',
				minCols: 2,
				maxCols: 4,
				minAdWidth: 0,
				possibleContentWidth: 0,
				isResponsiveGridBorder: false,
				initialData: this.$initialData
			};
		},
		watch: {
			selectedAd: function (val) {
				document.querySelector('body').setAttribute('data-ad-type', val);
				this.setColCase();
			}
		},
		methods: {
			setGridBorderWidth (isResponsiveGridBorder, minAdWidth) {
				// react only if grid gap is at it's maximum (first restore gap before border)
				if (isResponsiveGridBorder && this.calculatedGridGapWidth === this.gridColumnGap) {
					let possibleGridBorderWidth = (document.documentElement.clientWidth - minAdWidth - this.colCount * this.teaserWidth - (this.colCount - 1) * this.gridColumnGap) / 2;

					if (this.prevColCount > this.colCount) {
						let tempPossibleGridBorderWidth = possibleGridBorderWidth;
						possibleGridBorderWidth = (document.documentElement.clientWidth - minAdWidth - this.prevColCount * this.teaserWidth - (this.prevColCount - 1) * this.gridColumnGap) / 2;

						if (possibleGridBorderWidth < 0) {
							possibleGridBorderWidth = tempPossibleGridBorderWidth;
						}
					}

					this.calculatedGridBorderWidth = Math.max(this.gridBorderMinWidth, Math.min(this.gridBorder, possibleGridBorderWidth));
					document.documentElement.style.setProperty('--grid-border', this.calculatedGridBorderWidth + 'px');
				}
			},
			setGridColumnGapWidth (possibleContentWidth, minAdWidth, isResponsiveGridBorder) {
				// this block makes the grid gap smaler if needed as a last option after a possible shrinkage of the grid gap and all the outside ads
				// react only if grid border is at it's minimum (as second not first or parallel)
				if (this.gridColumnGapActive && (this.calculatedGridBorderWidth === this.gridBorderMinWidth || !isResponsiveGridBorder)) {
					// calcualte the new colCount with smaler gaps
					this.colCount = Math.floor((possibleContentWidth + this.gridColumnGapMinWidth) / (this.teaserWidth + this.gridColumnGapMinWidth));
					if (this.colCount > this.maxCols) {
						this.colCount = this.maxCols;
					}

					// calc the biggest possible gab
					let possibleGridGapWidth = (document.documentElement.clientWidth - minAdWidth - this.colCount * this.teaserWidth - 2 * this.calculatedGridBorderWidth) / (this.colCount - 1); // (1310-4*300-20)/3
					this.calculatedGridGapWidth = Math.max(this.gridColumnGapMinWidth, Math.min(this.gridColumnGap, possibleGridGapWidth));

					document.documentElement.style.setProperty('--grid-column-gap', this.calculatedGridGapWidth + 'px');

					// dispatch an event so the slider and others can react to just this change instead of listening to every resize
					let event = new CustomEvent('gridGapChanged', {
						bubbles: true,
						cancelable: true,
						detail: {
							colCount: this.colCount,
							gapWidth: this.calculatedGridGapWidth,
							source: 'App.vue'
						}
					});
					document.querySelector('body').dispatchEvent(event);
				}
			},
			setColCase () {
				this.checkAdOverlap();

				// get the possible content width (baseline)
				this.possibleContentWidth = document.documentElement.clientWidth - this.minAdWidth - this.gridBorderMinWidth * 2;
				this.colCount = Math.floor((this.possibleContentWidth + this.gridColumnGapMinWidth) / (this.teaserWidth + this.gridColumnGapMinWidth));
				if (this.colCount > this.maxCols) {
					this.colCount = this.maxCols;
				}

				this.isResponsiveGridBorder && this.setGridBorderWidth(this.isResponsiveGridBorder, this.minAdWidth);
				this.gridColumnGapActive && this.setGridColumnGapWidth(this.possibleContentWidth, this.minAdWidth, this.isResponsiveGridBorder);
				this.isResponsiveGridBorder && this.setGridBorderWidth(this.isResponsiveGridBorder, this.minAdWidth); // a second time because we react to grid gap in this function and in some cases we need to recalc

				document.documentElement.style.setProperty('--justify-grid-content', 'center');
				if (this.colCount > this.maxCols) {
					this.colCount = this.maxCols;
				} else if (this.colCount < this.minCols) {
					document.documentElement.style.setProperty('--justify-grid-content', 'start');

					if (this.isResponsiveGridBorder) {
						document.documentElement.style.setProperty('--grid-border', this.gridBorderMinWidth + 'px');
					}

					if (this.gridColumnGapActive) {
						document.documentElement.style.setProperty('--grid-column-gap', this.gridColumnGapMinWidth + 'px');
					}

					this.colCount = this.minCols;
				}

				// col count changed
				if (this.colCount !== this.prevColCount) {
					this.prevColCount = this.colCount;

					document.querySelector('body').setAttribute('data-col-count', this.colCount);
					let event = new CustomEvent('colCountChanged', {
						bubbles: true,
						cancelable: true,
						detail: {
							colCount: this.colCount,
							source: 'App.vue'
						}
					});
					document.querySelector('body').dispatchEvent(event);
					document.documentElement.style.setProperty('--col-count', this.colCount);
				}
			},
			touchDetector () {
				return !!('ontouchstart' in window || navigator.MaxTouchPoints > 0 || navigator.msMaxTouchPoints > 0);
			},
			setOutsideAdSize () {
				switch (this.selectedAd) {
					case 'bridge':
					case 'double-sitebar':
						this.minAdWidth = this.skyWidth * 2;
						this.minOutsideAdWidth = this.skyWidth;
						break;

					case 'flagship':
					case 'sitebar':
						this.minAdWidth = this.sitebarWidth;
						this.minOutsideAdWidth = this.sitebarWidth;
						break;

					default:
						this.minAdWidth = 0;
						break;
				}
			},
			checkResponsiveGrid () {
				// responsive gridborder active?
				this.isResponsiveGridBorder = this.selectedAd === 'sitebar' || this.selectedAd === 'double-sitebar' || this.selectedAd === 'billboard';

				// min width = default width (we don't go smaller)
				if (!this.isResponsiveGridBorder) {
					this.gridBorderMinWidth = this.gridBorder;
				}

				if (!this.gridColumnGapActive) {
					this.gridColumnGapMinWidth = this.gridColumnGap;
				}
			},
			checkAdOverlap () {
				// if the ad is allowed to get covered we can show more content
				if (this.adOverlap && this.selectedAd === 'sitebar') {
					let tempPossibleContentWidth = document.documentElement.clientWidth - this.minAdWidth - this.gridBorder * 2;
					let tempColCount = Math.floor((tempPossibleContentWidth + this.gridColumnGap) / (this.teaserWidth + this.gridColumnGap));

					if (tempColCount === 2) {
						this.setOutsideAdSize();
						this.minAdWidth = this.minAdWidth / 2;
					}
				}
			}
		},
		beforeCreate () {
			// disable the ability to kill the hp via an ad
			document.write = (str) => {
				if (window.location.href.includes('local')) {
					console.log('document.write ' + str);
				}
			};
			document.open = () => {
				if (window.location.href.includes('local')) {
					console.log('document.open');
				}
			};
			document.close = () => {
				if (window.location.href.includes('local')) {
					console.log('document.close');
				}
			};
		},
		created () {
			window.AdService = window.AdService || [];
			this.selectedAd = document.querySelector('body').getAttribute('data-ad-type');
			this.setOutsideAdSize();
			this.checkResponsiveGrid();

			window.addEventListener('resize', this.setColCase);
			this.setColCase();

			document.documentElement.style.setProperty('--sitebar-width', this.minOutsideAdWidth + 'px');
			document.documentElement.style.setProperty('--justify-grid-content', 'center');

			document.documentElement.style.setProperty('--screen-touchable', this.touchDetector());

			const response = window.initialData.serviceResponse.response;
			if (response.variant === 'sticky' || response.type === 'sitebar' || response.type === 'double-sitebar' || response.type === 'flagship') {
				this.sticky = true;
			}

			if (!window.SiteInfo) {
				window.SiteInfo = {};
			}
			window.SiteInfo.brand = process.env.VUE_APP_BRAND;
			window.SiteInfo.loginPosition = document.querySelector('html').hasAttribute('data-header-login') ? 'header' : 'content';

			if (location.hostname !== 'localhost') {
				const regex = /\.([^.]+?)$/;
				window.SiteInfo.tld = regex.exec(location.hostname)[1];
			}
			// get Top Level Domain
			if (window.SiteInfo.tld === 'lan' || location.hostname === 'localhost') {
				if (window.SiteInfo.brand === 'gmx') {
					window.SiteInfo.tld = 'net';
				} else {
					window.SiteInfo.tld = 'de';
				}
			}

			window.SiteInfo.brandShort = '';
			switch (window.SiteInfo.brand) {
				case 'webde':
					window.SiteInfo.brandShort = 'web';
					break;
				case 'gmx':
					window.SiteInfo.brandShort = 'gmx';
					break;
				case 'einsundeins':
					window.SiteInfo.brandShort = '1und1';
					break;
				default:
					window.SiteInfo.brandShort = 'localhost';
			}

			if (window.SiteInfo.brand === 'webde') {
				window.SiteInfo.portalName = 'webde';
			} else if (window.SiteInfo.brand === 'gmx') {
				window.SiteInfo.portalName = 'gmx' + window.PageConfig.country;
			} else if (window.SiteInfo.brand === 'einsundeins') {
				window.SiteInfo.portalName = '1und1';
			}

			if (window.initialData.serviceResponse.response.type === 'billboard' && window.initialData.serviceResponse.response.variant !== 'dynamic') {
				this.minCols = 3;
			}

			new CookielascheInterface();

			const initializeTracking = () => {
				window.Tracking = new TrackingInterface();
				new TrackingConfig();
			};

			initializeTracking();
		},
		mounted () {
			AdServiceInterface.init('service', undefined, this.colCount);
			this.selectedAd = AdServiceInterface.serviceResponse.response.type;

			AdServiceHelper.listenEvent('initialize.end', () => {
				AdServiceInterface.logVersion();
			});

			this.$nextTick(() => {
				/*
	               const makeBiddingAds = () => {
	                   const availableResponse = this.$initialData.availableResponse;
	                   PrebidInterface.init();
	                   if (response.rtb) {
	                       PrebidInterface.startBidding('initial');
	                   }

	                   if (availableResponse.placements.box_1 && availableResponse.placements.box_1.rtb) {
	                       PrebidInterface.startBidding('box_1');
	                   } else {
	                       AdServiceInterface.makeAd('box_1');
	                   }

	                   for (let i = 1; i <= 9; i++) {
	                       if (availableResponse.placements['box_' + i] && availableResponse.placements['box_' + i].rtb) {
	                           PrebidInterface.startBidding('box_' + i);
	                       } else {
	                           AdServiceInterface.makeAd('box_' + i);
	                       }
	                   }
	               };
	            */

				if (window.initialData.serviceResponse.response.bg) {
					const { color, clickurl } = window.initialData.serviceResponse.response.bg;
					color && document.documentElement.style.setProperty('--color-ac-bg', window.initialData.serviceResponse.response.bg.color);

					clickurl &&
						document.querySelectorAll('[data-ac], [data-container]').forEach((element) => {
							element.style.cursor = 'pointer';
							element.addEventListener('click', (event) => {
								window.open(clickurl, '_blank');
							});
						});
				}

				const makeAds = () => {
					for (let i = 1; i <= 10; i++) {
						AdServiceInterface.makeAd('box_' + i);
					}
				};

				const makeEventAds = (response) => {
					const parts = ['middle', 'left', 'right'];
					for (let part of parts) {
						const responsePart = response[part];
						if (responsePart) {
							let fillMe = '';
							let fallbackWidth = 0;
							let fallbackHeight = 0;

							// middle fallback is always billboard
							if (part === 'middle') {
								fallbackWidth = 970;
								fallbackHeight = 250;
							}

							if (AdServiceInterface.fallback) {
								this.fallbackWidth = true;
								if (responsePart.fallbackWidth) {
									fallbackWidth = responsePart.fallbackWidth;
								}
								if (responsePart.fallbackHeight) {
									fallbackHeight = responsePart.fallbackHeight;
								}

								if (part === 'middle') {
									fillMe = responsePart.fallback['column' + this.colCount + 'Ad'];
									const takeSmaller = (currentCols) => {
										const uimTag = AdServiceInterface.getUIMTag(part, fallbackWidth, fallbackHeight);
										let takeMe = responsePart.fallback['column' + currentCols + 'Ad'];
										if (!takeMe) {
											takeMe = responsePart.fallback['column' + (currentCols - 1) + 'Ad'];
										}
										return uimTag + takeMe;
									};

									document.addEventListener(
										'colCountChanged',
										(colChangeEvent) => {
											const colCase = colChangeEvent.detail.colCount;
											const takeMe = takeSmaller(colCase);
											AdServiceInterface.queHtml(part, uimTag + takeMe, true);
										},
										false
									);

									fillMe = takeSmaller(this.colCount);
								} else {
									fillMe = responsePart.fallback;
								}
							} else {
								if ((response.type === 'bridge' || response.type === 'flagship' || response.type === 'cinematic' || response.variant === 'dynamic') && part === 'middle') {
									this.dynamicTop = true;
								}
								if (response.skyVariant !== undefined && (response.skyVariant.length === 0 || response.skyVariant === 'dynamic')) {
									this.dynamicSides = true;
								}
								fillMe = responsePart.ad;
							}

							const uimTag = AdServiceInterface.getUIMTag(part, fallbackWidth, fallbackHeight);
							AdServiceInterface.queHtml(part, uimTag + fillMe, false, part);
						}
					}
				};

				const makeEventAdblockFallbackAds = () => {
					const callTop = ['billboard', 'flagship', 'bridge', 'cinematic'].indexOf(this.selectedAd) !== -1;
					const callLeft = ['double-sitebar'].indexOf(this.selectedAd) !== -1;
					const callRight = ['double-sitebar', 'sitebar'].indexOf(this.selectedAd) !== -1;

					if (callTop) {
						this.minCols = 3; // 3 cols minimum because be only have a billboard in adblock case
						this.setColCase(); // trigger for possible new colCase

						// TODO: refactoring, move to another place where it suits better
						// fixes "jumping" billboard
						const middleServiceSlot = document.querySelector('[data-service-slot="middle"]');
						if (middleServiceSlot) {
							middleServiceSlot.style.opacity = '0';
						}

						AdServiceHelper.listenEvent('ad.useiframecss', () => {
							if (middleServiceSlot) {
								middleServiceSlot.style.opacity = null;
							}
						});
						AdServiceInterface.makeAd('middle', true);
					}
					if (callLeft) {
						AdServiceInterface.makeAd('left', true);
					}
					if (callRight) {
						AdServiceInterface.makeAd('right', true);
					}
				};

				const makeRotationAds = (response, isPrivateMode) => {
					const callTop = this.selectedAd === 'billboard';
					const callRight = this.selectedAd === 'sitebar';

					if (callTop) {
						if (isPrivateMode) {
							AdServiceInterface.makeAd('middle', true);
						} else {
							AdServiceInterface.queHtml('middle', response.ad, false, response.type);
						}
					}

					if (callRight) {
						if (isPrivateMode) {
							AdServiceInterface.makeAd('right', true);
						} else {
							AdServiceInterface.queHtml('right', response.ad, false, response.type);
						}
					}
				};

				const callAfterAdBlockDect = (isAdBlock) => {
					if (!isAdBlock) {
						const callAfterPrivateModeDect = (isPrivateMode) => {
							AdServiceInterface.incrementAc();
							this.fallback = AdServiceInterface.showFallback();
							const response = AdServiceInterface.serviceResponse.response;

							if (isPrivateMode) {
								makeAds();
							} else {
								// if no event start prebid
								// makeBiddingAds();

								// there is no bidding right now
								makeAds();
							}

							if (!response.rtb) {
								if (response.middle || response.left || response.right) {
									makeEventAds(response);
								} else {
									makeRotationAds(response, isPrivateMode);
								}
							}

							AdServiceInterface.makePartnerlinks();
							AdServiceInterface.makeVorteilswelt();
							AdServiceInterface.makeAd('tam');
						};

						AdServiceInterface.checkPrivateMode(callAfterPrivateModeDect);
					} else {
						this.fallback = true;
						makeEventAdblockFallbackAds();
						makeAds();
						AdServiceInterface.makePartnerlinks();
						AdServiceInterface.makeVorteilswelt();
						AdServiceInterface.makeAd('tam');
					}
				};

				AdServiceInterface.checkAdBlock(callAfterAdBlockDect);

				document.querySelector(':root').style.setProperty('--scrollbar-width', window.innerWidth - document.body.offsetWidth + 'px');
			});
			this.$nextTick().then(() => {
				let event = new CustomEvent('pageReady', { bubbles: true, cancelable: true, detail: { source: 'App.vue' } });
				document.dispatchEvent(event);
			});
		}
	};
</script>
